import '././TestListComponent.scss';
import React, {useCallback} from 'react';
import {ITableColumn} from "../../../shared/models/table.model";
import {ITest} from "../../../shared/models/test.model";
import {CommonService} from "../../../shared/services";
import {APIConfig, ImageConfig} from "../../../constants";
import SwitchComponent from "../../../shared/components/core/switch/switchComponent";
import ChipComponent from "../../../shared/components/chip/ChipComponent";
import TableWrapperV2Component
    from "../../../shared/components/table-components/table-wrapper-v2/TableWrapperV2Component";
import {Link} from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import commonService from "../../../shared/services/common.service";
import TooltipComponent from "../../../shared/components/tooltip/tooltipComponent";
import {IAPIResponseType} from "../../../shared/models/api.model";
import {getTestListLite} from "../../../store/actions/staticData.action";

const MODULE_NAME = "TestList"

interface TestListTableComponentProps {
    filterData?: {};
}

const TestListTableComponent = (props: TestListTableComponentProps) => {

    const {filterData} = props;
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const dispatch = useDispatch();
    const columns: ITableColumn[] = [
        {
            id: 'id',
            label: 'ID',
            isDefaultColumn: true,
            fixed: "left",
        },
        {
            id: 'name', label: 'Test Name', isDefaultColumn: true,
            fixed: "left", width: "250",
            content: (item: ITest) => (
                <>
                    {
                        (item?.name).length > 23 ?
                            <TooltipComponent
                                tooltip={CommonService.capitalizeFirstLetterOfEachWord(item?.name)}
                                placement={"top"}
                                arrow={true}
                            >
                                <div className={"ellipses-for-table-data"}>
                                    {CommonService.capitalizeFirstLetterOfEachWord(item?.name)}
                                </div>
                            </TooltipComponent> :
                            <>
                                {CommonService.capitalizeFirstLetterOfEachWord(item?.name)}
                            </>
                    }
                </>
            )
        },
        {
            id: 'biomarkers', label: 'Biomarkers', isDefaultColumn: true,
            align: 'center',
            content: (item: ITest) => {
                const biomarkerList = item?.biomarker || [];
                return biomarkerList?.length > 0 ?
                    <TooltipComponent
                        placement={"top"}
                        arrow={true}
                        tooltip={biomarkerList?.map((biomarker: any) => <ChipComponent key={biomarker?.id}
                                                                                       label={biomarker?.name}/>)}>
                        <div>
                            {biomarkerList?.length}
                        </div>
                    </TooltipComponent> : 0
            }
        },
        {
            id: 'code', label: 'Test Code', isDefaultColumn: false,
            align: 'center',
            content: (item: ITest) => (
                <>
                    {
                        (item?.code).length > 10 ?
                            <TooltipComponent
                                tooltip={CommonService.capitalizeFirstLetterOfEachWord(item?.code)}
                                placement={"top"}
                                arrow={true}
                            >
                                <div className={"ellipses-for-table-data"}>
                                    {CommonService.capitalizeFirstLetterOfEachWord(item?.code)}
                                </div>
                            </TooltipComponent> :
                            <>
                                {CommonService.capitalizeFirstLetterOfEachWord(item?.code)}
                            </>
                    }
                </>
            )
        },
        {
            id: 'sampleType', label: 'Sample Type', isDefaultColumn: true, align: 'center',
            content: (item: ITest) => (
                <>
                    {
                        (item?.sampleType).length > 23 ?
                            <TooltipComponent
                                tooltip={CommonService.capitalizeFirstLetterOfEachWord(item?.sampleType)}
                                placement={"top"}
                                arrow={true}
                            >
                                <div className={"ellipses-for-table-data"}>
                                    {CommonService.capitalizeFirstLetterOfEachWord(item?.sampleType)}
                                </div>
                            </TooltipComponent> :
                            <>
                                {CommonService.capitalizeFirstLetterOfEachWord(item?.sampleType)}
                            </>
                    }
                </>
            )
        },
        {
            id: 'sampleCollectionDevice',
            label: 'Sample Collection Device',
            isDefaultColumn: false,
            width: "250",
            content: (item: ITest) => (
                <>
                    {
                        (item?.sampleCollectionDeviceName)?.length > 23 ?
                            <TooltipComponent
                                tooltip={CommonService.capitalizeFirstLetterOfEachWord(item?.sampleCollectionDeviceName)}
                                placement={"top"}
                                arrow={true}
                            >
                                <div className={"ellipses-for-table-data"}>
                                    {CommonService.capitalizeFirstLetterOfEachWord(item?.sampleCollectionDeviceName)}
                                </div>
                            </TooltipComponent> :
                            <>
                                {CommonService.capitalizeFirstLetterOfEachWord(item?.sampleCollectionDeviceName)}
                            </>
                    }
                </>
            )

        },
        {
            id: 'createdAt', label: 'Added Date & Time', isDefaultColumn: false, align: 'center', width: 180,
            content: (item: ITest) => (
                <>
                    {
                        item?.createdAt ?
                            <>
                                {commonService.formatDate(item?.createdAt)}
                                &nbsp;|&nbsp;
                                {commonService.formatTime(item?.createdAt)} <br/>
                            </> : <> - </>
                    }
                </>
            )
        },
        {
            id: 'onBoardedBy', label: 'Added by', isDefaultColumn: false, align: 'center',
            content: (item: ITest) => (
                <>
                    {item?.createdByDetails ? <>{CommonService.capitalizeFirstLetterOfEachWord(item?.createdByDetails?.firstName)} {CommonService.capitalizeFirstLetterOfEachWord(item?.createdByDetails?.lastName)}</> : '-'}
                </>
            )
        },
        {
            id: 'status', label: 'Panel Status', isDefaultColumn: true, align: 'center',
            width: '150',
            content: (item: ITest) => (
                <>
                    <ChipComponent className={`min-width-80 ${item?.statusObj?.code}`}
                                   variant={'outlined'}
                                   label={CommonService.capitalizeFirstLetter(item?.statusObj?.title)}/>
                </>
            )
        },
        // {
        //     id: 'xls', label: 'XLS Template', sortable: false, isDefaultColumn: true, align: "center",
        //     content: (item: ITest) => (
        //         item?.bulkImportTemplate?.url ?
        //             <ButtonComponent
        //                 variant={'icon'}
        //                 color={"secondary"}
        //                 handleClick={() => {
        //                     downloadResultExcel(item)
        //                 }}
        //             >
        //                 <DescriptionIcon/>
        //             </ButtonComponent> :
        //             <>-</>
        //     )
        // },

        {
            id: 'isActive',
            label: commonService.checkAccess('Test', 'testToggle', currentUser?.userAccessModules) ?
                'Active/Inactive' : '',
            width: commonService.checkAccess('Test', 'testToggle', currentUser?.userAccessModules) ?
                "150" : "10",
            align: "center",
            isDefaultColumn: true,
            content: (item: ITest, index: any) => (
                <React.Fragment>
                    {
                        commonService.checkAccess('Test', 'testToggle', currentUser?.userAccessModules) ?
                            <SwitchComponent
                                disabled={(item?.statusObj?.code === 'draft')}
                                size={"small"}
                                checked={item.isActive}
                                onUpdate={() => handleTestToggle(item, index)}/> :
                            <></>}
                </React.Fragment>
            )
        },
        {
            id: 'actions',
            label: '',
            isDefaultColumn: true,
            align: 'center',
            fixed: "right",
            width: "100",
            hideInColumnPreferences: true,
            content: (item: ITest, index: any) => (
                <React.Fragment>
                    <div className="table-actions" id={`view_testPanel_` + index}>
                        {item?.statusObj?.code === "draft" ? <>
                                <Link
                                    to={CommonService._routeConfig.TestCreateRoute() + `?testId=${item?.id}&step=report_type`}>
                                    <div className="table-action">
                                        <ChevronRightIcon/>
                                    </div>
                                </Link>
                            </>
                            :
                            <>
                                <Link to={CommonService._routeConfig.TestDetailsRoute(item?.id)}>
                                    <div className="table-action">
                                        <ChevronRightIcon/>
                                    </div>
                                </Link>
                            </>}
                    </div>
                </React.Fragment>
            )
        },
    ];

    // const downloadResultExcel = useCallback((item: any) => {
    //     CommonService.downloadFile(item.bulkImportTemplate.url, item.bulkImportTemplate.title || 'result.xlsx', 'xlsx')
    // }, []);

    const onToggleTest = useCallback((item: any, index: any) => {
        CommonService._test.toggleTestStatus(item.id, {})
            .then((response: IAPIResponseType<any>) => {
                CommonService._communications.TableWrapperRefreshSubject.next({
                    moduleName: MODULE_NAME
                });
                dispatch(getTestListLite())
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.error || `Error while ${item?.isActive ? "deactivating" : "activating"} the test panel`, "error");
            });
    }, [dispatch]);

    const handleTestToggle = useCallback((item: any, index: any) => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: `Are you sure you want to ${item?.isActive ? "deactivate" : "activate"} this test?`,
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.ConfirmationImage}`,
            direction: "up",
            yes: {
                text: `${item?.isActive ? "Deactivate" : "Activate"}`,
                color: "primary"
            },
            no: {
                text: "Cancel",
                color: "primary"
            }
        })
            .then(() => {
                onToggleTest(item, index);
            })
            .catch(() => {
            })
    }, [onToggleTest]);

    return (
        <div className="table-list-container">
            <TableWrapperV2Component
                isPaginated={true}
                columns={columns}
                url={APIConfig.TEST_LIST.URL}
                method={APIConfig.TEST_LIST.METHOD}
                extraPayload={filterData}
                isColumnFilterVisible={true}
                moduleName={MODULE_NAME}
            />
        </div>
    )
}
export default TestListTableComponent;
